import { UserIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CustomAttributeFields } from '@/components/account-profile/CustomAttributeFields';
import { ProfileImageField } from '@/components/account-profile/ProfileImageField';
import { Button } from '@/components/core/Button';
import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { evaluateNewPassword, passwordsMatch } from './passwordRules';
import { cloneUser, mergeUser, userBridge, userImageUrl, UserRecord } from './userBridge';

const fieldLabelClassName = 'tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base';

function toSentenceCase(value: string) {
    if (!value) {
        return value;
    }
    const lower = value.toLocaleLowerCase();
    return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
}

interface PasswordFormValues {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
}

const PasswordSection = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [saving, setSaving] = useState(false);
    const [confirmBlurred, setConfirmBlurred] = useState(false);
    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { dirtyFields, submitCount },
    } = useForm<PasswordFormValues>({
        defaultValues: {
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        },
    });
    const oldPassword = watch('oldPassword');
    const newPassword = watch('newPassword');
    const confirmNewPassword = watch('confirmNewPassword');
    const passwordRule = evaluateNewPassword(newPassword);
    const showPasswordErrors = Boolean(dirtyFields.newPassword) || submitCount > 0;
    const passwordsAreMatching = newPassword.length > 0 && passwordsMatch(newPassword, confirmNewPassword);
    const showMismatch = (confirmNewPassword.length > 0 || confirmBlurred)
        && !passwordsMatch(newPassword, confirmNewPassword);
    const canChangePassword = Boolean(oldPassword)
        && passwordRule.patternOk
        && passwordRule.lengthOk
        && passwordsAreMatching;

    const changePassword = (values: PasswordFormValues) => {
        const rule = evaluateNewPassword(values.newPassword);
        if (!values.oldPassword || !rule.patternOk || !rule.lengthOk || !passwordsMatch(values.newPassword, values.confirmNewPassword)) {
            return;
        }

        setSaving(true);
        const { userResource } = userBridge();
        userResource.changePassword({
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
        }, () => {
            toast.success(t('USER.CHANGE_PASSWORD_SUCCESS'));
            reset({
                oldPassword: '',
                newPassword: '',
                confirmNewPassword: '',
            });
            setConfirmBlurred(false);
            setSaving(false);
        }, (response) => {
            if (response.status === 400) {
                toast.error(t('USER.OLD_PASSWORD_WRONG'));
            } else {
                toast.error(t('USER.SAVE_ERROR'));
            }
            setSaving(false);
        });
    };

    return (
        <form
            className="tw-flex tw-flex-col tw-gap-4"
            onSubmit={handleSubmit(changePassword)}
            noValidate
        >
            <h2 className="tw-m-0 tw-text-t-sm tw-font-medium tw-text-t-primary lg:tw-text-t-base">
                {toSentenceCase(t('USER.PASSWORD'))}
            </h2>
            <div className="tw-flex tw-flex-col tw-gap-1">
                <label htmlFor="old-password" className={fieldLabelClassName}>
                    {t('USER.OLD_PASSWORD')}
                </label>
                <Input
                    id="old-password"
                    type="password"
                    autoComplete="off"
                    {...register('oldPassword')}
                />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
                <label htmlFor="new-password" className={fieldLabelClassName}>
                    {t('NEW_PASSWORD')}
                </label>
                <Input
                    id="new-password"
                    type="password"
                    autoComplete="off"
                    {...register('newPassword')}
                />
                {showPasswordErrors && passwordRule.showLengthError && (
                    <p role="alert" className="tw-m-0 tw-text-t-xs tw-text-error lg:tw-text-t-sm">
                        {t('SIGNUP.PASSWORD_LENGTH')}
                    </p>
                )}
                {showPasswordErrors && passwordRule.showPatternError && (
                    <p role="alert" className="tw-m-0 tw-text-t-xs tw-text-error lg:tw-text-t-sm">
                        {t('SIGNUP.PASSWORD_VALIDATION_MESSAGE')}
                    </p>
                )}
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1">
                <label htmlFor="confirm-new-password" className={fieldLabelClassName}>
                    {t('NEW_PASSWORD_CONFIRMATION')}
                </label>
                <Input
                    id="confirm-new-password"
                    type="password"
                    autoComplete="off"
                    {...register('confirmNewPassword', {
                        onBlur: () => setConfirmBlurred(true),
                    })}
                />
                {showMismatch && (
                    <p role="alert" className="tw-m-0 tw-text-t-xs tw-text-error lg:tw-text-t-sm">
                        {t('SIGNUP.PASSWORDS_DONT_MATCH')}
                    </p>
                )}
            </div>
            <div className="tw-flex tw-justify-end">
                <Button id="change-password-submit" type="submit" disabled={saving || !canChangePassword} loading={saving}>
                    {t('USER.CHANGE_PASSWORD')}
                </Button>
            </div>
        </form>
    );
};

function UserPage() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const originalRef = useRef<UserRecord>({});
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isSsoUser, setIsSsoUser] = useState(false);
    const methods = useForm<UserRecord>({
        defaultValues: {
            userId: '',
            email: '',
            firstName: '',
            lastName: '',
            customValues: {},
        },
    });
    const { formState: { isDirty }, handleSubmit, register, reset, setValue, watch } = methods;
    const imageUrl = watch('imageUrl');
    const systemAccount = Boolean(watch('systemAccount'));
    const managingAccount = Boolean(watch('managingAccount'));
    const showPassword = ready && !isSsoUser && !systemAccount && !managingAccount;

    useEffect(() => {
        const { userResource, rootScope, singleSignOnResource } = userBridge();
        const applyUser = (user: UserRecord) => {
            const snapshot = cloneUser(user);
            originalRef.current = snapshot;
            reset(cloneUser(user));
            setReady(true);
        };

        singleSignOnResource.get({}, (response) => {
            setIsSsoUser(Boolean(response?.protocol));
        });

        if (rootScope.user?.userId) {
            applyUser(rootScope.user);
        }

        userResource.get({}, (response) => {
            applyUser(mergeUser(rootScope, response));
        }, () => {
            setReady(true);
        });
    }, [reset]);

    const cancel = () => {
        reset(cloneUser(originalRef.current));
    };

    const save = (values: UserRecord) => {
        const payload: UserRecord = {
            ...originalRef.current,
            ...values,
        };
        setSaving(true);
        const { userResource, rootScope } = userBridge();
        userResource.save(payload, () => {
            toast.success(t('USER.SAVE_SUCCESS'));
            userResource.get({}, (response) => {
                const stored = mergeUser(rootScope, response);
                const snapshot = cloneUser(stored);
                originalRef.current = snapshot;
                reset(cloneUser(stored));
                rootScope.$broadcast('userAccountUpdated', rootScope.user);
                setSaving(false);
            }, () => {
                rootScope.$broadcast('userAccountUpdated', rootScope.user);
                setSaving(false);
            });
        }, () => {
            toast.error(t('USER.SAVE_ERROR'));
            setSaving(false);
        });
    };

    if (!ready) {
        return (
            <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
                <Loader />
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
                <Icon icon={UserIcon} size="sm" variant="dark" />
                <h1 className="tw-m-0 tw-text-t-base tw-font-medium tw-text-t-primary lg:tw-text-t-lg">
                    {t('USER_DATA')}
                </h1>
            </div>
            <div className="tw-rounded-lg tw-bg-white tw-p-5">
                <div className="tw-flex tw-flex-col tw-gap-6 md:tw-flex-row md:tw-items-start">
                    <ProfileImageField
                        variant="user"
                        canEdit
                        hasImage={Boolean(imageUrl)}
                        imageUrl={typeof imageUrl === 'string' ? imageUrl : undefined}
                        onUploaded={() => {
                            const { rootScope, userResource } = userBridge();
                            const url = userImageUrl();
                            setValue('imageUrl', url, { shouldDirty: false });
                            rootScope.user = rootScope.user || {};
                            rootScope.user.imageUrl = url;
                            rootScope.$broadcast('updateUserData');
                            rootScope.loadContacts?.();
                            userResource.get({}, (response) => {
                                const stored = mergeUser(rootScope, response);
                                if (stored.imageUrl) {
                                    setValue('imageUrl', stored.imageUrl, { shouldDirty: false });
                                }
                            });
                        }}
                    />

                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-6">
                        <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={handleSubmit(save)}>
                            <h2 className="tw-m-0 tw-text-t-sm tw-font-medium tw-text-t-primary lg:tw-text-t-base">
                                {t('USER.INFO')}
                            </h2>
                            <div className="tw-flex tw-flex-col tw-gap-1">
                                <label htmlFor="userUserName" className={fieldLabelClassName}>
                                    {t('USERNAME')}
                                </label>
                                <Input
                                    id="userUserName"
                                    readOnly
                                    {...register('userId')}
                                />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1">
                                <label htmlFor="userEmail" className={fieldLabelClassName}>
                                    {t('EMAIL')}
                                </label>
                                <Input
                                    id="userEmail"
                                    readOnly
                                    {...register('email')}
                                />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1">
                                <label htmlFor="userFirstName" className={fieldLabelClassName}>
                                    {t('FIRST_NAME')}
                                </label>
                                <Input
                                    id="userFirstName"
                                    placeholder={t('FIRST_NAME')}
                                    autoFocus
                                    {...register('firstName')}
                                />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1">
                                <label htmlFor="userLastName" className={fieldLabelClassName}>
                                    {t('LAST_NAME')}
                                </label>
                                <Input
                                    id="userLastName"
                                    placeholder={t('LAST_NAME')}
                                    {...register('lastName')}
                                />
                            </div>

                            <FormProvider {...methods}>
                                <CustomAttributeFields variant="user" canEdit />
                            </FormProvider>

                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <Button id="change-data-submit" type="submit" disabled={saving || !isDirty} loading={saving}>
                                    {t('ITEM.SAVE_CHANGES')}
                                </Button>
                                <Button
                                    id="user_cancel"
                                    type="button"
                                    variant="secondary"
                                    disabled={saving || !isDirty}
                                    onClick={cancel}
                                >
                                    {t('CANCEL')}
                                </Button>
                            </div>
                        </form>

                        {showPassword && (
                            <>
                                <hr className="tw-m-0 tw-w-full tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-tertiary" />
                                <PasswordSection />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserPage;
